import { useState } from "react";
import { CrudTable, StatusBadge, Card } from "@/components/shared";
import { inr } from "@/lib/api";

const OUTCOMES = ["Confirmed Will Pay", "Disputing Amount", "Unreachable", "Payment Made"];
const STATUSES = ["Current", "Overdue 0-15", "Overdue 16-30", "Overdue 30+", "Cleared", "Written Off"];

function Aging({ rows }) {
  const pending = rows.filter((r) => r.status !== "Cleared" && r.status !== "Written Off");
  const buckets = { "Current": [], "Overdue 0-15": [], "Overdue 16-30": [], "Overdue 30+": [] };
  pending.forEach((r) => { if (buckets[r.status]) buckets[r.status].push(r); });
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {Object.entries(buckets).map(([name, items]) => {
        const total = items.reduce((s, r) => s + (r.total_cost - r.amount_paid), 0);
        const accent = name === "Current" ? "#059669" : name === "Overdue 0-15" ? "#C9A24B" : "#DC2626";
        return (
          <Card key={name} data-testid="aging-bucket" className="p-4">
            <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{name}</p>
            <p className="font-display text-2xl font-light mt-2" style={{ color: accent }}>{inr(total)}</p>
            <p className="text-xs text-slate-500 mt-1">{items.length} patient(s)</p>
          </Card>
        );
      })}
    </div>
  );
}

export default function Dues() {
  const [rows, setRows] = useState([]);
  const columns = [
    { key: "patient_name", label: "Patient" },
    { key: "branch", label: "Branch" },
    { key: "total_cost", label: "Total", render: (r) => <span className="font-mono-ync">{inr(r.total_cost)}</span> },
    { key: "amount_paid", label: "Paid", render: (r) => <span className="font-mono-ync text-[#34d399]">{inr(r.amount_paid)}</span> },
    { key: "due", label: "Due", render: (r) => <span className="font-mono-ync text-[#f87171]">{inr(r.total_cost - r.amount_paid)}</span> },
    { key: "due_date", label: "Due Date", render: (r) => r.due_date?.slice(0, 10) || "—" },
    { key: "payment_plan", label: "Plan" },
    { key: "last_contact_date", label: "Last Contact", render: (r) => r.last_contact_date?.slice(0, 10) || "—" },
    { key: "contact_outcome", label: "Outcome" },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
  ];
  const fields = [
    { key: "patient_id", label: "Patient (linked)", type: "patient", full: true },
    { key: "patient_name", label: "Patient Name (or type manually)", type: "text" },
    { key: "branch", label: "Branch", type: "select", optionsKey: "branches" },
    { key: "total_cost", label: "Total Treatment Cost (₹)", type: "number", min: 0 },
    { key: "amount_paid", label: "Amount Paid (₹)", type: "number", min: 0 },
    { key: "recovered", label: "Recovered Now (₹)", type: "number", min: 0 },
    { key: "promise_date", label: "Promise to Pay Date", type: "date" },
    { key: "due_date", label: "Due Date", type: "date" },
    { key: "payment_plan", label: "Payment Plan", type: "text" },
    { key: "last_contact_date", label: "Last Contact Date", type: "date" },
    { key: "contact_outcome", label: "Contact Outcome", type: "select", options: OUTCOMES },
    { key: "next_followup_date", label: "Next Follow-Up Date", type: "date" },
    { key: "status", label: "Status", type: "select", options: STATUSES },
    { key: "remark", label: "Remark", type: "textarea", full: true },
  ];
  return (
    <CrudTable
      testid="dues" endpoint="dues" dateField="due_date"
      title="Pending Dues Clarity"
      subtitle="One searchable, aging-aware view. Any due not contacted in 7+ days pushes to the notification bell."
      columns={columns} fields={fields}
      defaults={{ total_cost: 0, amount_paid: 0, status: "Current" }}
      onData={setRows}
      extraTop={<Aging rows={rows} />}
    />
  );
}
